import { LayoutDefinition, LayoutEntry } from './layout'
import { PANELS, PanelId } from './registry'
import { reorder } from './reorder'
import { PanelSpan } from './spans'

const replaceAt = (panels: LayoutEntry[], index: number, entry: LayoutEntry): LayoutEntry[] =>
  panels.map((current, position) => (position === index ? entry : current))

// A panel already placed elsewhere swaps with this slot rather than appearing twice.
export const showPanelAt = (layout: LayoutDefinition, index: number, panel: PanelId): LayoutDefinition => {
  const current = layout.panels[index]

  if (!current || current.panel === panel) {
    return layout
  }

  const existing = layout.panels.findIndex((entry) => entry.panel === panel)

  if (existing === -1) {
    return { panels: replaceAt(layout.panels, index, { ...current, panel }) }
  }

  return {
    panels: layout.panels.map((entry, position) => {
      if (position === index) {
        return { ...entry, panel }
      }
      if (position === existing) {
        return { ...entry, panel: current.panel }
      }
      return entry
    }),
  }
}

export const resizePanelAt = (layout: LayoutDefinition, index: number, span: PanelSpan): LayoutDefinition => {
  const current = layout.panels[index]

  return current ? { panels: replaceAt(layout.panels, index, { ...current, span }) } : layout
}

export const movePanelTo = (layout: LayoutDefinition, from: number, to: number): LayoutDefinition => {
  const panels = reorder(layout.panels, from, to)

  return panels === layout.panels ? layout : { panels }
}

// Emptying the layout is allowed; `reconcileLayout` falls back to the shipped default on the next load.
export const removePanelAt = (layout: LayoutDefinition, index: number): LayoutDefinition => {
  if (!layout.panels[index]) {
    return layout
  }

  return { panels: layout.panels.filter((_entry, position) => position !== index) }
}

export const addPanel = (layout: LayoutDefinition, panel: PanelId): LayoutDefinition => {
  if (layout.panels.some((entry) => entry.panel === panel)) {
    return layout
  }

  return { panels: [...layout.panels, { panel, span: PANELS[panel].defaultSpan }] }
}

// One save, not a loop of single calls — each derives from the layout passed in, so only the last would apply.
export const addPanels = (layout: LayoutDefinition, panels: readonly PanelId[]): LayoutDefinition => {
  const placed = new Set(layout.panels.map((entry) => entry.panel))
  const additions = panels
    .filter((panel) => !placed.has(panel))
    .map((panel) => ({ panel, span: PANELS[panel].defaultSpan }))

  return additions.length > 0 ? { panels: [...layout.panels, ...additions] } : layout
}

/** Never drops to zero panels; refuses the whole call rather than removing some. */
export const removePanels = (layout: LayoutDefinition, panels: readonly PanelId[]): LayoutDefinition => {
  const remaining = layout.panels.filter((entry) => !panels.includes(entry.panel))

  return remaining.length > 0 ? { panels: remaining } : layout
}

// `undefined` clears it: `JSON.stringify` omits the key rather than storing `null`.
export const configurePanelAt = (
  layout: LayoutDefinition,
  index: number,
  config: Record<string, unknown> | undefined
): LayoutDefinition => {
  const current = layout.panels[index]

  return current ? { panels: replaceAt(layout.panels, index, { ...current, config }) } : layout
}
